import { jaLocale, translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import {
  background,
  black,
  gray01,
  gray02,
  gray04,
  gray06,
  primary,
} from 'presentations/views/styles/color';
import * as tracker from 'presentations/views/helpers/analytics';

import Icon from 'presentations/views/components/atoms/Icon';
import Link from 'presentations/views/components/atoms/Link';
import Image from 'presentations/views/components/atoms/Image';
import {
  bestPracticesListPath,
  engineeringListPath,
  newsListPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import JobBoardBanner from '../../molecules/OwnedContentBanner/JobBoardBanner';

export interface Props {}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const onClickSocial = () => {
    tracker.setEvent('click sns account', {
      position: 'resource sidebar',
    });
  };

  const router = useRouter();
  return (
    <>
      <FixedSidebarItem>
        <JobBoardContainer>
          <JobBoardBanner />
        </JobBoardContainer>
      </FixedSidebarItem>
      <SidebarItem>
        <SidebarItemTitle>
          <SidevarItemTitleIcon name="folder-outline" color="primary" />
          {t('category')}
        </SidebarItemTitle>
        <SiderbarListContainer>
          <CategoryContainer href={newsListPath()}>
            <CategoryIcon name="minus" />
            <CategoryTitle>{t(`news`)}</CategoryTitle>
          </CategoryContainer>
          <CategoryContainer href={bestPracticesListPath()}>
            <CategoryIcon name="minus" />
            <CategoryTitle>{t(`best practice`)}</CategoryTitle>
          </CategoryContainer>
          <CategoryContainer href={engineeringListPath()}>
            <CategoryIcon name="minus" />
            <CategoryTitle>{t(`engineering`)}</CategoryTitle>
          </CategoryContainer>
        </SiderbarListContainer>
      </SidebarItem>

      {router.locale === jaLocale && (
        <SidebarItem>
          <SidebarItemTitle>
            <SidevarItemTitleIcon
              name="share-variant-outline"
              color="primary"
            />
            {t('sns')}
          </SidebarItemTitle>
          <BrandLinkList>
            <BrandLinkListItem>
              <SocialLink
                href="https://twitter.com/jicoohq"
                target="_blank"
                onClick={onClickSocial}
                rel="noopener"
              >
                <SocialLogo
                  src={'mails/logo_twitter.png'}
                  alt={translate('mailer.Footer.Twitter')}
                />
              </SocialLink>
              <SocialLink
                href="https://www.facebook.com/jicoohq"
                target="_blank"
                onClick={onClickSocial}
                rel="noopener"
              >
                <SocialLogo
                  src={'mails/logo_facebook.png'}
                  alt={translate('mailer.Footer.Facebook')}
                />
              </SocialLink>
            </BrandLinkListItem>
          </BrandLinkList>
          <BrandLinkText>{t('sns description')}</BrandLinkText>
        </SidebarItem>
      )}
    </>
  );
}

const SidebarItem = styled.div`
  margin-bottom: ${spacing(56)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(28)};
  }
`;
const FixedSidebarItem = styled(SidebarItem)`
  background-color: ${background};
  position: sticky;
  top: 96px;
  ${mediaMax.tablet} {
    position: static;
  }
`;

const JobBoardContainer = styled.div`
  overflow: hidden;
  border-radius: ${borderRadius(6)};
`;
const SidebarItemTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 0 ${spacing(20)} 0;
  margin: 0 0 ${spacing(24)} 0;
  border-bottom: 1px solid ${gray01};

  font-size: ${fontSize(16)};
  line-height: 150%;
  font-weight: bold;

  ${mediaMax.tablet} {
    padding: 0 0 ${spacing(12)} 0;
    font-size: ${fontSize(14)};
  }
`;

const SidevarItemTitleIcon = styled(Icon)`
  margin: 0 ${spacing(8)} 0 0;
  line-height: 24px;

  ${mediaMax.tablet} {
    margin: 0 ${spacing(6)} 0 0;
    font-size: ${fontSize(20)};
    line-height: 20px;
  }
`;

const SiderbarListContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryContainer = styled(Link)`
  color: ${black};
  display: flex;
  flex-direction: row;
  justify-content: middle;
  padding: ${spacing(20)} 0;
  border-bottom: 1px solid ${gray04};

  &:first-of-type {
    padding: 0 0 ${spacing(12)} 0;
  }
`;

const CategoryIcon = styled(Icon)`
  margin: 0 ${spacing(8)} 0 0;
  width: 18px !important;
  height: 18px !important;
  line-height: 22px;

  ${mediaMax.tablet} {
    margin-top: 3px;
    font-size: ${fontSize(12)};
    line-height: 12px;
  }
`;

const CategoryTitle = styled.div`
  font-size: ${fontSize(14)};
  line-height: 160%;
  letter-spacing: 0.05em;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;

const BrandLinkList = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(24)} 0;
    :last-of-type {
      margin: 0;
    }
    padding: 0 ${spacing(16)} 0 0;
    width: 33.33%;
    display: block;
  }
  ${mediaMax.smartphone} {
    padding: 0;
    width: auto;
    text-align: center;
  }
`;
const BrandLinkListItem = styled.div`
  ${mediaMax.smartphone} {
    display: block;
    margin-bottom: ${spacing(20)};
  }
`;
const BrandLinkText = styled.p`
  margin-top: ${spacing(20)};
  text-align: center;
`;
const SocialLogo = styled(Image)`
  width: 48px;
  height: 48px;
`;
const SocialLink = styled(Link)`
  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${spacing(8)};

  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const TagListItem = styled(Link)`
  color: ${gray01};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${gray06};
  border-radius: ${borderRadius(16)};
  margin-right: ${spacing(8)};
  margin-bottom: ${spacing(8)};
  padding: ${spacing(8)};
`;
const TagListItemLoading = styled.div`
  margin-right: ${spacing(8)};
  margin-bottom: ${spacing(8)};
`;
const TagIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${gray02};
  width: 14px !important;
  height: 14px !important;
`;
const TagName = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;
const MoreItem = styled(Link)`
  color: ${black};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
const MoreIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
`;
const MoreName = styled.div``;
